/**
 * experiment.ts -- the research-only `experiment` overlay renderer, with no DOM (card t_42cdbde6).
 *
 * GEOMETRY IN, SHAPES OUT (2026-10-14, U7 LC-2). The payload is what U5 serves for one pane window
 * (`GET /api/overlays/{asset}/{tf}/experiment`). This module picks which events get full geometry
 * (the selected one) and clamps shapes to the window's first bar; it never reads bars, never
 * recomputes `outcome_class` from `net_r` and never computes a price (BR1.3, BR1.6). A `net_r`
 * of 0 is whatever class the payload says it is.
 *
 * THIN BODIES (card t_5b4bfe85). A window whose full body is over the server's size cap (the
 * research 4h and 1D pages of htf_mss_dol) arrives with `thin: true`: every event, but only the
 * selected one with levels, windows and flags. Nothing drawn changes, because the rest of this
 * module never reads those fields of a non-selected event; the pane row says the body was thin.
 *
 * BOUNDED BY CONSTRUCTION (PD-5). One pass over `p.events`: exactly three bands (TP box, stop box,
 * entry line) per traded event that carries entry, stop and tp, one marker per event, and levels
 * and windows for the selected event only. A dense window of every event's levels would cost
 * `events x levels` bands per repaint; the selected-only rule keeps it at `3 x events + k`.
 *
 * THE STRINGS ARE UNTRUSTED (SD-2). The pane rows are HTML (`main.ts` hands them to `kv`), so the
 * experiment id and event ids go through `escapeHtml`. Plates are painted on a canvas, not HTML.
 *
 * Imported by `overlays.ts` as a re-export (Plan Approval decision 1, option A), so `main.ts` reads
 * it from `./overlays` like every other renderer.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';
import { escapeHtml } from './navigator';

export type ExperimentLevel = {
  price: number;
  origin_time: number;
  origin_tf?: string;
  kind: string;
  /** U5 serves `null` when the export has no label (deviation 2); the plate is `label || kind`. */
  label?: string | null;
};

export type ExperimentWindow = { start_time: number; end_time: number; label: string };

/** One drawn event. `side` and `outcome_class` also carry `label` (deviation 1). In a THIN body
 *  (card t_5b4bfe85) every event but the selected one carries only the eight fields this module
 *  draws a non-selected event from (`experiments.THIN_KEYS`); the rest are absent, never null. */
export type ExperimentEvent = {
  time: number;
  event_id: string;
  side: string;
  reason?: string;
  outcome_class: string;
  entry: number | null;
  stop: number | null;
  tp: number | null;
  exit_time: number | null;
  exit_price?: number | null;
  barrier?: string | null;
  net_r?: number | null;
  flags?: Record<string, unknown>;
  levels?: ExperimentLevel[];
  windows?: ExperimentWindow[];
};

/** The fields of a thin row, equal to `chartapp/api/experiments.py` THIN_KEYS (pinned by
 *  test_events_store.py::test_thin_keys_match_the_renderer). */
export const THIN_KEYS = ['time', 'event_id', 'side', 'outcome_class', 'entry', 'stop', 'tp', 'exit_time'] as const;

export type ExperimentCounts = { total: number; win: number; loss: number; skip: number; label?: number };

export type ExperimentPayload = {
  component: 'experiment';
  asset: string;
  tf: string;
  source: string;
  exp: string;
  events: ExperimentEvent[];
  counts: ExperimentCounts;
  /** The full body was over the server's size cap, so the events came thin: levels, windows
   *  and flags for the `selected` event only. Absent from an older API build. */
  thin?: boolean;
  sealed_dropped: number;
};

export type ExperimentDrawing = {
  bands: ZoneBand[];
  markers: SeriesMarker<Time>[];
  rows: [string, string][];
  /** Level lines drawn for the selected event (the pane's `data-exp-levels` diagnostic). */
  selectedLevels: number;
  /** The selected event when this window holds it, else null (the pane's `data-exp-event`). */
  selectedId: string | null;
  /** The drawn selected event, for the navigator's detail block. */
  selected: ExperimentEvent | null;
};

export const WIN_COLOR = 'rgba(38, 166, 154, 0.95)';
export const LOSS_COLOR = 'rgba(239, 83, 80, 0.95)';
export const SKIP_COLOR = 'rgba(158, 158, 158, 0.95)';
export const WINDOW_FILL = 'rgba(120, 144, 156, 0.10)';
/** Box fill alphas: [TP, SL] for the selected event and for the others ([Q2]). */
export const TP_FILL_ALPHA = { selected: 0.28, other: 0.10 };
export const SL_FILL_ALPHA = { selected: 0.12, other: 0.05 };
/** Border alpha of a non-selected box: reduced, never zero, so a box stays visible. */
const OTHER_BORDER_ALPHA = 0.45;

export type LevelStyle = { color: string; dashed: boolean };

/** Level colour and dash by `kind`. The two kinds the tree's exports carry today
 *  (`research/flow_windows`: `fix_ref`, `pre30_ref`) plus the common reference levels. */
export const LEVEL_STYLE: Record<string, LevelStyle> = {
  fix_ref: { color: 'rgba(255, 193, 7, 0.95)', dashed: false },
  pre30_ref: { color: 'rgba(171, 71, 188, 0.95)', dashed: true },
  pdh: { color: 'rgba(66, 165, 245, 0.95)', dashed: false },
  pdl: { color: 'rgba(66, 165, 245, 0.95)', dashed: false },
  open: { color: 'rgba(255, 255, 255, 0.85)', dashed: true },
};
export const LEVEL_STYLE_DEFAULT: LevelStyle = { color: 'rgba(189, 189, 189, 0.9)', dashed: true };

const TRADED = new Set(['long', 'short']);

/** `rgba(r, g, b, a)` with its alpha replaced. */
function withAlpha(rgba: string, alpha: number): string {
  return rgba.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
}

function outcomeColor(outcome: string): string {
  if (outcome === 'win') return WIN_COLOR;
  if (outcome === 'loss') return LOSS_COLOR;
  return SKIP_COLOR;
}

/** Stop distance in pips; null when any input is null (SD-7: a missing value is not a zero). */
export function stopPips(entry: number | null, stop: number | null, pip: number | null): number | null {
  if (entry === null || stop === null || pip === null || !(pip > 0)) return null;
  return Math.abs(entry - stop) / pip;
}

/** Target distance in pips; null when any input is null. */
export function tpPips(entry: number | null, tp: number | null, pip: number | null): number | null {
  if (entry === null || tp === null || pip === null || !(pip > 0)) return null;
  return Math.abs(tp - entry) / pip;
}

/**
 * The shapes of one pane window (BR1.1-BR1.6).
 *
 * `windowFirstTime` is the pane's first bar: RangePrimitive cannot anchor a shape before the
 * series, so an earlier `fromTime` moves to it and a shape that ends before it is dropped (BR1.5).
 * With null, nothing is clamped.
 */
export function experimentDrawing(p: ExperimentPayload, windowFirstTime: number | null,
  selectedId: string | null): ExperimentDrawing {
  const first = windowFirstTime;
  const clamp = (t: number): UTCTimestamp => (first !== null && t < first ? first : t) as UTCTimestamp;
  const ends = (t: number | null): boolean => first === null || t === null || t >= first;
  const bands: ZoneBand[] = [];
  const markers: SeriesMarker<Time>[] = [];
  const rows: [string, string][] = [];
  const exp = escapeHtml(p.exp);
  let selected: ExperimentEvent | null = null;
  let selectedLevels = 0;

  for (const e of p.events) {
    const isSel = selectedId !== null && e.event_id === selectedId;
    if (isSel) selected = e;
    const traded = TRADED.has(e.side);
    const color = traded ? outcomeColor(e.outcome_class) : SKIP_COLOR;
    markers.push({
      time: e.time as UTCTimestamp,
      position: e.side === 'long' ? 'belowBar' : 'aboveBar',
      shape: e.side === 'long' ? 'arrowUp' : e.side === 'short' ? 'arrowDown' : 'circle',
      color,
      size: isSel ? 2 : 1,
    });
    if (traded) {
      if (e.entry === null || e.stop === null || e.tp === null) {
        rows.push([`experiment ${exp}`, `${escapeHtml(e.event_id)} has no entry/stop/tp`]);
      } else if (ends(e.exit_time)) {
        const from = clamp(e.time);
        const to = e.exit_time === null ? null : (e.exit_time as UTCTimestamp);
        const border = isSel ? color : withAlpha(color, OTHER_BORDER_ALPHA);
        const tpA = isSel ? TP_FILL_ALPHA.selected : TP_FILL_ALPHA.other;
        const slA = isSel ? SL_FILL_ALPHA.selected : SL_FILL_ALPHA.other;
        bands.push({
          lo: Math.min(e.entry, e.tp), hi: Math.max(e.entry, e.tp), fromTime: from, toTime: to,
          fill: withAlpha(color, tpA), border, label: 'TP', exactSpan: true,
        });
        bands.push({
          lo: Math.min(e.entry, e.stop), hi: Math.max(e.entry, e.stop), fromTime: from, toTime: to,
          fill: withAlpha(color, slA), border, label: 'SL', exactSpan: true,
        });
        bands.push({
          lo: e.entry, hi: e.entry, fromTime: from, toTime: to,
          fill: border, border, label: 'entry', dashed: true, exactSpan: true,
        });
      }
    }
    if (!isSel) continue;
    for (const lv of e.levels ?? []) {
      if (!ends(e.time)) continue;
      const st = LEVEL_STYLE[lv.kind] ?? LEVEL_STYLE_DEFAULT;
      bands.push({
        lo: lv.price, hi: lv.price, fromTime: clamp(lv.origin_time), toTime: e.time as UTCTimestamp,
        fill: st.color, border: st.color, label: lv.label || lv.kind, dashed: st.dashed, exactSpan: true,
      });
      selectedLevels += 1;
    }
    for (const w of e.windows ?? []) {
      if (!ends(w.end_time)) continue;
      bands.push({
        lo: 0, hi: 0, fromTime: clamp(w.start_time), toTime: w.end_time as UTCTimestamp,
        fill: WINDOW_FILL, border: WINDOW_FILL, label: w.label, exactSpan: true, fullHeight: true,
      });
    }
  }

  markers.sort((x, y) => Number(x.time) - Number(y.time));
  const c = p.counts;
  const value = c.total === 0
    ? 'no events in this window'
    : `${c.total} events, ${c.win} win / ${c.loss} loss / ${c.skip} skip`
      + `${c.label ? `, ${c.label} label` : ''}, sealed_dropped ${p.sealed_dropped}`
      + (p.thin ? ' (thin rows: levels of the selected event only)' : '');
  rows.unshift([`experiment ${exp}`, value]);
  return { bands, markers, rows, selectedLevels, selectedId: selected ? selected.event_id : null, selected };
}
